import { useCallback, useEffect } from "react";
import InfiniteLoader from "react-window-infinite-loader";

import ListEntitiesNew from "components/ListEntitiesNew";
import { EmptystateHelmetColored } from "components/icons/generated";
import LinkButton from "ds/components/Button/LinkButton";
import { isPathnameDifferent } from "ds/components/DrawerNew/utils";
import EmptyState from "ds/components/EmptyState";
import { AnalyticsPagePolicy } from "hooks/useAnalytics/pages/policy";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useURLParams from "hooks/useURLParams";
import { Policy } from "types/generated";
import { showPolicyEditDrawer } from "views/Account/PolicyEditDrawer";
import { SpacesContext } from "views/Account/SpacesProvider";
import { AccountContext } from "views/AccountWrapper";

import { showPolicyCreateDrawer } from "../../PolicyCreateDrawer";
import PoliciesViewHeader from "../ViewHeader";
import useTakenPolicyNames from "../useTakenPolicyNames";
import PoliciesListFiltersLayout from "./FiltersLayout";
import PoliciesListVirtualizedItem from "./VirtualizedItem";
import { ENTITIES_PER_PAGE, REFETCH_QUERIES_ON_DELETE } from "./constants";
import useSearchPolicies from "./useSearchPolicies";
import useSearchPoliciesSuggestions from "./useSearchPoliciesSuggestions";

const PoliciesList = () => {
  const { hasEntityCreateAccess: canCreatePolicy } = useTypedContext(SpacesContext);
  const { accountName } = useTypedContext(AccountContext);
  const urlParams = useURLParams();

  const handleOpenUpdateDrawer = (policy: Policy) => {
    showPolicyEditDrawer({
      id: policy.id,
      analyticsPage: AnalyticsPagePolicy.PoliciesList,
      takenPolicyNames: takenPolicyNames,
      policy,
      refetchQueries: REFETCH_QUERIES_ON_DELETE,
    });
  };

  useTitle(`Policies · ${accountName}`);

  const {
    entities,
    error,
    isPageEmpty,
    hasNoFilteringResults,
    loading,
    isItemLoaded,
    loadMoreItems,
  } = useSearchPolicies();

  const {
    filters,
    filtersMap,
    sortOptions,
    loading: filtersLoading,
    refetchActiveSections,
    filteredCount,
  } = useSearchPoliciesSuggestions();

  const takenPolicyNames = useTakenPolicyNames();

  const openCreateDrawer = useCallback(
    (omitQuerySearchCheck?: boolean) => {
      showPolicyCreateDrawer({
        takenPolicyNames,
        shouldCloseOnLocationChange: omitQuerySearchCheck ? isPathnameDifferent : undefined,
      });
    },
    [takenPolicyNames]
  );

  useEffect(() => {
    if (urlParams.has("create-new")) {
      openCreateDrawer(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  return (
    <>
      <PoliciesViewHeader />
      <PoliciesListFiltersLayout
        openCreateDrawer={openCreateDrawer}
        isPageEmpty={isPageEmpty}
        renderedEntitiesCount={entities.length}
        loadingIndication={loading}
        filters={filters}
        filtersMap={filtersMap}
        sortOptions={sortOptions}
        filtersLoading={filtersLoading}
        refetchActiveSections={refetchActiveSections}
        filteredCount={filteredCount}
        canCreatePolicy={canCreatePolicy}
      >
        {isPageEmpty && (
          <EmptyState
            title="You don’t have any policies yet"
            icon={EmptystateHelmetColored}
            caption={`Policies are declarative rules evaluated at various decision points within Spacelift.
            Create new policy from scratch using button above or start with one of our templates.`}
          >
            {canCreatePolicy && (
              <LinkButton variant="secondary" to="/policies-templates">
                Explore templates
              </LinkButton>
            )}
          </EmptyState>
        )}
        {hasNoFilteringResults && (
          <EmptyState
            title="No results"
            icon={EmptystateHelmetColored}
            caption="Try different filters."
            announce
          />
        )}
        {!!entities.length && (
          <InfiniteLoader
            isItemLoaded={isItemLoaded}
            itemCount={entities.length + ENTITIES_PER_PAGE}
            loadMoreItems={loadMoreItems}
          >
            {({ onItemsRendered }) => (
              <ListEntitiesNew
                itemCount={entities.length}
                itemProps={{
                  items: entities,
                  onEdit: handleOpenUpdateDrawer,
                }}
                virtualizedItem={PoliciesListVirtualizedItem}
                itemKey={(index) => entities[index].id}
                onItemsRendered={onItemsRendered}
              />
            )}
          </InfiniteLoader>
        )}
      </PoliciesListFiltersLayout>
    </>
  );
};

export default PoliciesList;
